<script setup>
import { ref, watch } from 'vue'
import { apolloClient } from 'boot/apollo'
import { useMeta } from 'quasar'
import referenceQuery from 'src/graphql/referenceQuery.gql'
import { useRoute } from 'vue-router'
import B2CContactPopup from 'components/home/B2CContactPopup.vue'
import ReferencesSwiper from 'components/reference/ReferencesSwiper.vue'
import AppButton from '#/general/AppButton.vue'

const props = defineProps({ url: String })
const reference = ref(null)
const route = useRoute()

watch(() => props.url, () => {
  loadReference()
})

async function loadReference () {
  try {
    const referenceData = await apolloClient.query({
      query: referenceQuery,
      variables: {
        url: props.url
      }
    })
    reference.value = JSON.parse(JSON.stringify(referenceData?.data?.reference))
  } catch (e) {
    console.log('REFERENCE ERROR: ', JSON.parse(JSON.stringify(e)))
  }
}
await loadReference()

if (reference.value) {
  useMeta({
    title: `Reference - ${reference.value && reference.value.seo && reference.value.seo.title ? reference.value.seo.title : reference.value.title}`,
    meta: {
      description: { name: 'description', content: reference.value && reference.value.seo && reference.value.seo.description ? reference.value.seo.description : '' },
      keywords: { name: 'description', content: reference.value && reference.value.seo && reference.value.seo.keyword ? reference.value.seo.keyword : '' },
      canonical: { rel: 'canonical', href: `https://raumpioniere.ch/referenzen/${props.url}` },
      'og:title': { property: 'og:title', content: reference.value.seo.ogTitle ? reference.value.seo.ogTitle : reference.value.seo.title },
      'og:image': { property: 'og:image', content: reference.value.seo.ogImage ? reference.value.seo.ogImage : 'https://raumpioniere.ch/icons/favicon-128x128.png' },
      'og:url': { property: 'og:url', content: 'https://raumpioniere.ch/referenzen/' + props.url },
      'og:description': { property: 'og:description', content: reference.value.seo.ogDescription ? reference.value.seo.ogDescription : reference.value.seo.description },
      'og:type': { property: 'og:type', content: reference.value.seo.ogType ? reference.value.seo.ogType : 'Blog page' },
      'og:locale': { property: 'og:locale', content: 'de_CH' }
    }
  })
}
</script>

<template lang="pug">
.routerPageSize(v-if="reference")
  .reference
    b2-c-contact-popup(always)
    .reference--section
      .reference--section-title(v-html="reference.title")
      .reference--section-boxes
        .reference--section-box
          .reference--section-box-title Leistungen:
          .reference--section-box-content(v-html="reference.services")
        .reference--section-box
          .reference--section-box-title Herausforderung:
          .reference--section-box-content(v-html="reference.challenges")
    img.reference--main-img(:src="`https://raumpionierestorage.blob.core.windows.net/images/${reference.img}`" v-if="reference.img")
    .reference--section
      .reference--section-boxes
        .reference--section-box
          .reference--section-box-title Bauherr:
          .reference--section-box-content(v-html="reference.builder")
        .reference--section-box
          .reference--section-box-title Architektur:
          .reference--section-box-content(v-html="reference.architect")
        .reference--section-box
          .reference--section-box-title Weiteres:
          .reference--section-box-content(v-html="reference.additional")
        .reference--section-box
          .reference--section-box-title Bilder:
          .reference--section-box-content(v-html="reference.imageMadeBy")
      .reference--section-subtitle(v-html="reference.other")
    .reference--images
      template(v-for="image in reference.images" :key="image")
        img.reference--image(:src="`https://raumpionierestorage.blob.core.windows.net/images/${ image }`" v-if="image")
    .reference--related-title(v-if="reference.relatedReferences && reference.relatedReferences.length > 0" :class="!reference.relatedReferences || reference.relatedReferences.length === 0 || !reference.images || reference.images.length === 0 ? 'reference--related-title-no-padding' : ''") Weitere Referenzprojekte:
    references-swiper.reference--related-swiper(:references="reference.relatedReferences" v-if="reference.relatedReferences && reference.relatedReferences.length > 0")
    .reference--cta-btn-container(v-if="reference.cta && reference.cta.label")
      app-button.reference--cta-btn(
        :label="reference.cta.label"
        :to="reference.cta.to"
        padding="0 44px"
      )
</template>

<style lang="sass">
.reference
  @media screen and (min-width: 1024px)
    padding: 136px 0 116px
  @media screen and (max-width: 1024px)
    padding: 30px 0
  .reference--related-title-no-padding
    @media screen and (min-width: 1024px)
      margin: 0px 0 76px !important
    @media screen and (max-width: 1024px)
      margin: 0px 0 40px !important
  .reference--section
    @media screen and (min-width: 1024px)
      display: grid
      grid-template-columns: 1fr 1fr
      grid-gap: 15px
      margin-bottom: 70px
    @media screen and (max-width: 1024px)
    .reference--section-title
      font-weight: 500
      line-height: 130%
      color: #22223C
      @media screen and (min-width: 1024px)
        font-size: 55px
      @media screen and (max-width: 1024px)
        font-size: 30px
        margin-bottom: 30px
    .reference--section-subtitle
      line-height: 150%
      color: #22223C
      @media screen and (min-width: 1024px)
        font-size: 20px
      @media screen and (max-width: 1024px)
        font-size: 16px
        margin: 10px 0 40px
    .reference--section-boxes
      .reference--section-box
        @media screen and (min-width: 1024px)
          margin-bottom: 30px
        @media screen and (max-width: 1024px)
          margin-bottom: 15px
        .reference--section-box-title
          font-weight: 600
          line-height: 150%
          color: #22223C
          @media screen and (min-width: 1024px)
            font-size: 20px
          @media screen and (max-width: 1024px)
            font-size: 16px
        .reference--section-box-content
          font-weight: 400
          line-height: 150%
          color: #22223C
          @media screen and (min-width: 1024px)
            font-size: 20px
          @media screen and (max-width: 1024px)
            font-size: 16px

  .reference--main-img
    max-width: 100%
    @media screen and (min-width: 1024px)
      margin: 0px 0 100px
    @media screen and (max-width: 1024px)
      margin: 20px 0 30px
  .reference--images
    display: grid
    grid-gap: 15px
    @media screen and (min-width: 1024px)
      grid-template-columns: 1fr 1fr
    @media screen and (max-width: 1024px)
      grid-template-columns: 1fr
    .reference--image
      width: 100%
      max-width: 100%
  .reference--related-title
    font-weight: 500
    line-height: 130%
    text-align: center
    color: #22223C
    @media screen and (min-width: 1024px)
      font-size: 34px
      margin: 137px 0 76px
    @media screen and (max-width: 1024px)
      font-size: 20px
      margin: 50px 0 40px
  .reference--cta-btn-container
    width: 100%
    display: flex
    justify-content: center
    align-items: center
    margin: 50px 0 85px
    .reference--cta-btn

</style>
